import { createContext, useMemo, useReducer } from 'react'
import { Task } from '@/services/task/task.model'
import { listToMap, treeNodePath } from '@/utils/utils'
import { produce } from 'immer'
import { ID } from '@/services/common.model'

export interface TaskState {
  taskType: 'open' | 'closed'
  checkedFields: string[]
  taskList: Task.Info[]
}

export const initialState: TaskState = {
  taskType: 'open',
  checkedFields: [],
  taskList: []
}

export interface TaskAction {
  type: string
  payload?: any
}

export const SET_STATE = 'set data'
export function setTaskState(payload: any) {
  return {
    type: SET_STATE,
    payload
  }
}

export const SET_TASK_LIST = 'set task list'
export function setTaskListState(payload: Task.Info[]) {
  return {
    type: SET_TASK_LIST,
    payload
  }
}

export const NEW_TASK = 'new task'
export function newTaskState(payload: any) {
  return {
    type: NEW_TASK,
    payload
  }
}

export const DELETE_TASK = 'delete task'
export function deleteTaskState(payload: ID) {
  return {
    type: DELETE_TASK,
    payload
  }
}

export const UPDATE_TASK = 'update task'
export function updateTaskState(payload: Task.Info) {
  return {
    type: UPDATE_TASK,
    payload
  }
}

export const resolveTaskItem = (taskMap, task) => ({ ...task, path: treeNodePath(taskMap, task) })

const reducer = (state = initialState, action: TaskAction): TaskState => {
  const { type, payload } = action

  switch (type) {
    case SET_STATE: {
      return produce(state, (draft) => {
        Object.assign(draft, payload)
      })
    }
    case SET_TASK_LIST: {
      return produce(state, (draft) => {
        draft.taskList = payload
      })
    }
    case NEW_TASK: {
      return produce(state, (draft) => {
        const taskMap = listToMap(draft.taskList)
        const taskItem = resolveTaskItem(taskMap, payload)
        draft.taskList.push(taskItem)
      })
    }
    case DELETE_TASK: {
      return produce(state, (draft) => {
        draft.taskList = draft.taskList.filter((task) => task.id !== payload)
      })
    }
    case UPDATE_TASK: {
      return produce(state, (draft) => {
        const target = draft.taskList.find((item) => item.id === payload.id)
        if (target) {
          Object.assign(target, payload)
        }
      })
    }
    default:
      return state
  }
}

export interface TaskStore extends TaskState {
  dispatch: React.Dispatch<TaskAction>
}

export const TaskContext = createContext<TaskStore>({
  ...initialState,
  dispatch: () => {}
})

export const useTaskStore = (data = {}) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...data })
  const store = useMemo<TaskStore>(() => ({ ...state, dispatch }), [state])

  return store
}
